import { useCallback, useMemo } from "react";

import { compareFractionalOrder } from "@/lib/compare-fractional-order";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQuery } from "@tanstack/react-query";

import { getPriorityListForWorkspaceQueryDocument } from "./priority.gql";

export interface UseGetPriorityListProps {
  workspaceId?: string | undefined;
  enabled?: boolean;
}

export const useGetPriorityListForWorkspace = ({ workspaceId, enabled = true }: UseGetPriorityListProps) => {
  const queryResult = useQuery({
    queryKey: queryKeys.priorities.list(workspaceId ?? ""),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error("Workspace ID is required");
      }

      const client = createOperationClient("GetPriorityListForWorkspace");
      const data = await client.request(getPriorityListForWorkspaceQueryDocument, {
        input: { workspaceId },
      });

      return data.priorityListForWorkspace;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes for list data
  });

  const priorityList = useMemo(() => {
    return [...(queryResult.data ?? [])].sort((a, b) => compareFractionalOrder(a.order, b.order));
  }, [queryResult.data]);

  const findPriorityById = useCallback(
    (id: string) => {
      return priorityList.find((p) => p.id === id);
    },
    [priorityList],
  );

  return {
    priorityList,
    findPriorityById,
    loading: queryResult.isLoading,
    error: queryResult.error,
  };
};
